import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addDoc,
collection,
doc,
getDoc,
getDocs,
query,
where,
serverTimestamp,
setDoc,
updateDoc,
} from "firebase/firestore";
import { db } from "../lib/firebase";

function unitSlugToId(slug = "") {
  return String(slug).replace(/-/g, "");
}

function unitSlugToLabel(slug = "") {
  return String(slug)
    .split("-")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function itemKey(item = {}) {
  return String(item.itemId || item.kitItemId || item.id || item.itemName || "");
}

function mergeRequestItems(existingItems = [], newItems = []) {
  const map = new Map();

  [...existingItems, ...newItems].forEach((item) => {
    const key = itemKey(item);

    if (!map.has(key)) {
      map.set(key, {
        ...item,
        qtyRequested: Number(item.qtyRequested || 0),
      });
      return;
    }

    const current = map.get(key);

    map.set(key, {
      ...current,
      ...item,
      qtyRequested:
        Number(current.qtyRequested || 0) + Number(item.qtyRequested || 0),
    });
  });

  return Array.from(map.values()).filter(
    (item) => Number(item.qtyRequested || 0) > 0
  );
}

function updateInventoryItemsFromRequests(existingItems = [], requestItems = []) {
  const requestMap = new Map();

  requestItems.forEach((item) => {
    requestMap.set(itemKey(item), item);
  });

  return existingItems.map((item) => {
    const key = String(item.kitItemId || item.id || item.itemName || "");
    const requestItem = requestMap.get(key);

    if (requestItem) {
      const par = Number(item.parLevel || requestItem.parLevel || 0);
      const missingQty = Number(requestItem.qtyRequested || 0);
      const currentQty = Math.max(0, par - missingQty);

      return {
        ...item,
        currentQty,
        countedQty: currentQty,
        missingQty,
        status: missingQty > 0 ? "below_par" : "at_par",
        checkStatus: missingQty > 0 ? "below_par" : "par",
      };
    }

    if (item.type === "nestedKit" && Array.isArray(item.nestedItems)) {
      const updatedNestedItems = item.nestedItems.map((nested) => {
        const nestedKey = String(
          nested.kitItemId || nested.id || nested.itemName || ""
        );

        const nestedRequest = requestMap.get(nestedKey);

        if (!nestedRequest) return nested;

        const par = Number(nested.parLevel || nestedRequest.parLevel || 0);
        const missingQty = Number(nestedRequest.qtyRequested || 0);
        const currentQty = Math.max(0, par - missingQty);

        return {
          ...nested,
          currentQty,
          countedQty: currentQty,
          missingQty,
          status: missingQty > 0 ? "below_par" : "at_par",
          checkStatus: missingQty > 0 ? "below_par" : "par",
        };
      });

      const stillMissing = updatedNestedItems.some(
        (nested) => Number(nested.missingQty || 0) > 0
      );

      return {
        ...item,
        nestedItems: updatedNestedItems,
        missingQty: stillMissing ? 1 : 0,
        status: stillMissing ? "below_par" : "at_par",
        checkStatus: stillMissing ? "below_par" : "par",
      };
    }

    return item;
  });
}

function buildInventoryItemsFromRequestItems(requestItems = []) {
  return requestItems.map((item) => {
    const par = Number(item.parLevel || 0);
    const missingQty = Number(item.qtyRequested || 0);
    const currentQty = Math.max(0, par - missingQty);

    return {
      id: item.itemId,
      kitItemId: item.itemId,
      inventoryStockId: item.inventoryStockId || "",
      itemName: item.itemName || "",
      category: item.category || "",
      unitType: item.unitType || "each",
      kitLocationName: item.kitLocationName || "Other",
      parLevel: par,
      currentQty,
      countedQty: currentQty,
      replenishedQty: 0,
      missingQty,
      status: missingQty > 0 ? "below_par" : "at_par",
      checkStatus: missingQty > 0 ? "below_par" : "par",
    };
  });
}

export default function ResponderSupplyRequestPage() {
  const { unitId } = useParams();
  const navigate = useNavigate();

  const locationId = useMemo(() => unitSlugToId(unitId), [unitId]);
  const unitLabel = useMemo(() => unitSlugToLabel(unitId), [unitId]);
  const requestDate = useMemo(() => todayKey(), []);

  const [items, setItems] = useState([]);
  const [requestQtys, setRequestQtys] = useState({});
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadUnitItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  async function loadUnitItems() {
  try {
    setLoading(true);

   const assignmentSnap = await getDocs(
  query(
    collection(db, "inventoryKitAssignments"),
    where("locationSlug", "==", locationId)
  )
);

    if (assignmentSnap.empty) {
      setItems([]);
      return;
    }

    const allItems = [];

    for (const assignmentDoc of assignmentSnap.docs) {
  const assignment = assignmentDoc.data();

  if (assignment.active === false) continue;
  if (!assignment.templateId) continue;

      const kitSnap = await getDoc(
        doc(db, "inventoryKitTemplates", assignment.templateId)
      );

      if (!kitSnap.exists()) continue;

      const kit = kitSnap.data();

      for (const kitLocation of kit.locations || []) {
        for (const item of kitLocation.items || []) {

          // Skip checklist items
          if (item.type === "checkItem") continue;

          // Handle component kits
          if (item.type === "nestedKit" && item.nestedTemplateId) {

            const nestedSnap = await getDoc(
              doc(db, "inventoryKitTemplates", item.nestedTemplateId)
            );

            if (nestedSnap.exists()) {
              const nestedKit = nestedSnap.data();

              for (const nestedLocation of nestedKit.locations || []) {
                for (const nestedItem of nestedLocation.items || []) {

                  if (nestedItem.type === "checkItem") continue;

                  allItems.push({
                    id: nestedItem.id,
                    kitItemId: nestedItem.id,
                    inventoryStockId:
                      nestedItem.inventoryStockId || "",
                    itemName: nestedItem.itemName || "",
                    category: nestedItem.category || "",
                    unitType: nestedItem.unitType || "each",
                    kitLocationName:
                      `${kitLocation.name} → ${nestedLocation.name}`,
                    parLevel: Number(
                      nestedItem.parLevel || 0
                    ),
                  });
                }
              }
            }

            continue;
          }

          // Normal supply item
          allItems.push({
            id: item.id,
            kitItemId: item.id,
            inventoryStockId: item.inventoryStockId || "",
            itemName: item.itemName || "",
            category: item.category || "",
            unitType: item.unitType || "each",
            kitLocationName: kitLocation.name || "Other",
            parLevel: Number(item.parLevel || 0),
          });
        }
      }
    }

    allItems.sort((a, b) =>
      String(a.itemName || "").localeCompare(
        String(b.itemName || "")
      )
    );

    setItems(allItems);

  } catch (err) {
    console.error("Failed to load supply request items:", err);
    alert("Failed to load supply list.");
  } finally {
    setLoading(false);
  }
}

  function changeQty(itemId, delta) {
    setRequestQtys((prev) => {
      const current = Number(prev[itemId] || 0);
      const next = Math.max(0, current + delta);

      return {
        ...prev,
        [itemId]: next,
      };
    });
  }

  async function submitRequest() {
  const selectedItems = items
    .map((item) => {
      const id = item.kitItemId || item.id;

      return {
        itemId: id,
        inventoryStockId: item.inventoryStockId || "",
        itemName: item.itemName || "",
        category: item.category || "",
        unitType: item.unitType || "each",
        kitLocationName: item.kitLocationName || "Other",
        parLevel: Number(item.parLevel || 0),
        qtyRequested: Number(requestQtys[id] || 0),
      };
    })
    .filter((item) => item.qtyRequested > 0);

  if (selectedItems.length === 0) {
    alert("Select at least one item.");
    return;
  }

  try {
    setSaving(true);

    const inventoryId = `${locationId}_${requestDate}`;
    const inventoryRef = doc(db, "dailyInventories", inventoryId);
    const inventorySnap = await getDoc(inventoryRef);

    const openRequestSnap = await getDocs(
      query(
        collection(db, "supplyRequests"),
        where("locationId", "==", locationId),
        where("status", "==", "pending")
      )
    );

    let requestRef = null;
    let existingRequestItems = [];

    if (!openRequestSnap.empty) {
      requestRef = doc(db, "supplyRequests", openRequestSnap.docs[0].id);
      existingRequestItems = openRequestSnap.docs[0].data().items || [];
    }

    const mergedRequestItems = mergeRequestItems(
      existingRequestItems,
      selectedItems
    );

    const existingInventoryItems = inventorySnap.exists()
      ? inventorySnap.data().items || []
      : [];

    const updatedInventoryItems =
      existingInventoryItems.length > 0
        ? updateInventoryItemsFromRequests(
            existingInventoryItems,
            mergedRequestItems
          )
        : buildInventoryItemsFromRequestItems(mergedRequestItems);

    await setDoc(
      inventoryRef,
      {
        locationId,
        locationName: unitLabel,
        inventoryDate: requestDate,
        status: inventorySnap.exists()
          ? inventorySnap.data().status || "in_progress"
          : "in_progress",
        actionRequired: true,
        items: updatedInventoryItems,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    if (requestRef) {
      await updateDoc(requestRef, {
        items: mergedRequestItems,
        notes: notes.trim(),
        updatedAt: serverTimestamp(),
      });
    } else {
      await addDoc(collection(db, "supplyRequests"), {
        locationId,
        locationName: unitLabel,
        requestDate,
        status: "pending",
        notes: notes.trim(),
        items: mergedRequestItems,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    alert("Supply request submitted and inventory updated.");
    navigate(`/responder/${unitId}`);
  } catch (err) {
    console.error("Failed to submit supply request:", err);
    alert("Failed to submit supply request.");
  } finally {
    setSaving(false);
  }
}

  if (loading) {
    return <div style={styles.page}>Loading supply request...</div>;
  }

  const selectedCount = Object.values(requestQtys).reduce(
    (sum, qty) => sum + Number(qty || 0),
    0
  );

 const filteredItems = items.filter((item) =>
  item.itemName.toLowerCase().includes(searchTerm.toLowerCase())
);

const groupedItems = filteredItems.reduce((groups, item) => {
    const section = item.kitLocationName || "Other";
    if (!groups[section]) groups[section] = [];
    groups[section].push(item);
    return groups;
  }, {});

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button
          style={styles.backButton}
          onClick={() => navigate(`/responder/${unitId}`)}
        >
          Back
        </button>

        <div style={styles.titleBlock}>
  <h1 style={styles.title}>Request Supplies</h1>
          <p style={styles.subtitle}>
            {unitLabel} supplies only • {selectedCount} item(s) requested
          </p>
        </div>

        <button
          style={styles.submitButton}
          onClick={submitRequest}
          disabled={saving}
        >
          {saving ? "Sending..." : "Submit"}
        </button>
      </div>

      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Optional note for Logistics..."
        style={styles.notes}
      />

      <input
  value={searchTerm}
  onChange={(e) => setSearchTerm(e.target.value)}
  placeholder="Search for an item..."
  style={styles.searchInput}
/>

      <div style={styles.list}>
        {Object.entries(groupedItems).map(([sectionName, sectionItems]) => (
          <section key={sectionName} style={styles.section}>
            <h2 style={styles.sectionTitle}>{sectionName}</h2>

            <div style={styles.sectionList}>
              {sectionItems.map((item) => {
                const id = item.kitItemId || item.id;
                const qty = Number(requestQtys[id] || 0);
                const rowStyle =
                  qty > 0
                    ? { ...styles.row, ...styles.rowSelected }
                    : styles.row;

                return (
                  <div key={`${id}_${item.kitLocationName}`} style={rowStyle}>
                    <div style={styles.itemInfo}>
                      <div style={styles.itemName}>{item.itemName}</div>

                      <div style={styles.meta}>
                        {item.category || "Uncategorized"}
                      </div>

                      <div style={styles.meta}>PAR {item.parLevel || 0}</div>
                    </div>

                    <div style={styles.qtyControls}>
                      <button
                        style={styles.qtyButton}
                        onClick={() => changeQty(id, -1)}
                      >
                        −
                      </button>

                      <div style={styles.qtyNumber}>{qty}</div>

                      <button
                        style={styles.qtyButton}
                        onClick={() => changeQty(id, 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

const isPhone = window.innerWidth <= 640;
const isTablet = window.innerWidth <= 1024;

const styles = {
 page: {
  minHeight: "100vh",
  padding: isPhone ? 10 : 16,
  background: "#eef4ff",
  color: "#0f172a",
},

titleBlock: {
  gridColumn: isPhone ? "1 / -1" : "auto",
  gridRow: isPhone ? 1 : "auto",
},

 header: {
  display: "grid",
  gridTemplateColumns: isPhone ? "1fr 1fr" : "110px 1fr 130px",
  gap: 10,
  alignItems: "center",
  marginBottom: 16,
},

 title: {
  margin: 0,
  fontSize: isPhone ? 30 : 34,
  fontWeight: 900,
  textAlign: "center",
},

  subtitle: {
    margin: 0,
    color: "#64748b",
    textAlign: "center",
    fontWeight: 700,
  },

  backButton: {
    height: 52,
    borderRadius: 12,
    border: "none",
    background: "#475569",
    color: "#fff",
    fontWeight: 900,
    fontSize: 16,
    cursor: "pointer",
    gridColumn: isPhone ? "1" : "auto",
    gridRow: isPhone ? 2 : "auto",
  },

  submitButton: {
    height: 52,
    borderRadius: 12,
    border: "none",
    background: "#15803d",
    color: "#fff",
    fontWeight: 900,
    fontSize: 16,
    cursor: "pointer",
    gridColumn: isPhone ? "2" : "auto",
    gridRow: isPhone ? 2 : "auto",
  },

  notes: {
    width: "100%",
    minHeight: 64,
    marginBottom: 16,
    borderRadius: 16,
    border: "1px solid #cbd5e1",
    padding: 14,
    fontSize: 20,
    boxSizing: "border-box",
  },

  list: {
    display: "grid",
    gap: 16,
  },

  section: {
    background: "#fff",
    borderRadius: 18,
    border: "1px solid #dbe4ee",
    padding: 14,
  },

  sectionTitle: {
    margin: "0 0 12px",
    fontSize: 24,
    fontWeight: 900,
    color: "#0f172a",
  },

  sectionList: {
    display: "grid",
    gap: 10,
  },

 row: {
  background: "#f8fafc",
  border: "2px solid #cbd5e1",
  borderRadius: 14,
  padding: isPhone ? 12 : 14,
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 14,
  alignItems: "center",
},

  rowSelected: {
    background: "#dcfce7",
    borderColor: "#15803d",
  },

  itemInfo: {
    minWidth: 0,
  },

  itemName: {
  fontWeight: 900,
  fontSize: isPhone ? 20 : 20,
  color: "#0f172a",
  lineHeight: 1.15,
  wordBreak: "normal",
  overflowWrap: "break-word",
},

  meta: {
    color: "#64748b",
    fontWeight: 700,
    marginTop: 2,
    fontSize: 14,
  },

 qtyControls: {
  display: "grid",
  gridTemplateColumns: "1fr 70px 1fr",
  alignItems: "center",
  gap: 10,
  width: "100%",
},

 qtyButton: {
  height: 52,
  width: "100%",
  border: "none",
  borderRadius: 12,
  background: "#0f766e",
  color: "#fff",
  fontSize: 28,
  fontWeight: 900,
  cursor: "pointer",
  WebkitAppearance: "none",
  appearance: "none",
},

  qtyNumber: {
  textAlign: "center",
  fontSize: 30,
  fontWeight: 900,
  color: "#0f172a",
},

searchInput: {
  width: "100%",
  height: 56,
  marginBottom: 16,
  borderRadius: 16,
  border: "1px solid #cbd5e1",
  padding: "0 14px",
  fontSize: isPhone ? 18 : 20,
  boxSizing: "border-box",
  fontWeight: 700,
},
};